// jQuery free
import {createPopper} from '@popperjs/core/lib/popper-lite';
import preventOverflow from '@popperjs/core/lib/modifiers/preventOverflow';
import offset from '@popperjs/core/lib/modifiers/offset';
import {clearBodyLocks, lock} from 'tua-body-scroll-lock';
import {onDOMReady} from './dom';

const DATA_DROPDOWN_INIT = 'data-dropdown-init';
const DATA_DROPDOWN_ID = 'data-dropdown-id';
const DATA_DROPDOWN_PLACEMENT = 'data-dropdown-placement';
const DATA_DROPDOWN_DELAY = 'data-dropdown-delay';
const DATA_DROPDOWN_REFERENCE_ID = 'data-dropdown-reference-id';
export const DATA_DROPDOWN_ON_HOVER = 'data-dropdownonhover';
const CLASS_ACTIVE = '--active';
const CLASS_OVERLAY_ACTIVE = '--overlayActive';
const CLASS_OVERLAY_DISABLE = '--overlayDisabled';

export const EVENT_DROPDOWN_OPEN = 'kvik_event_dropdown_open';
export const EVENT_DROPDOWN_CLOSE = 'kvik_event_dropdown_close';


/**
 * @type {Dropdown[]}
 */
const allDropdowns = [];

/**
 * @type {Dropdown|null}
 */
let openedDropdown = null;

/**
 * @returns {Dropdown|null}
 */
export const getOpenedDropdown = () => {
    return openedDropdown;
};

const closeOpenedDropdown = (immediate = false) => {
    if (openedDropdown) {
        openedDropdown.close(immediate);
    }
};

/**
 * @param {HTMLElement} openerElement
 * @param {HTMLElement} dropdownElement
 */
export function createDropdown(openerElement, dropdownElement) {
    new Dropdown(openerElement, dropdownElement);
}



/**
 * @property {HTMLElement} referenceElement
 * @property {HTMLElement} dropdownElement
 * @property {HTMLElement} dropdownContentElement
 */
class Dropdown {
    /**
     * @param {HTMLElement} openerElement
     * @param {HTMLElement} dropdownElement
     */
    constructor(openerElement, dropdownElement) {
        allDropdowns.push(this);
        this.popperInstance = null;
        this.openerElement = openerElement;
        this.referenceElement = (openerElement.hasAttribute(DATA_DROPDOWN_REFERENCE_ID) ? document.getElementById(openerElement.getAttribute(DATA_DROPDOWN_REFERENCE_ID)) : null) || openerElement;
        this.dropdownElement = dropdownElement;
        if (!this.dropdownElement) {
            console.error('Dropdown element not found for reference element:', openerElement);
            return;
        }
        this.dropdownContentElement = dropdownElement.children[0];
        this.isReferenceElementInHeader = Boolean(this.referenceElement.closest('#layoutHeader')) || Boolean(this.referenceElement.closest('#mobileNav'));
        this.isInput = openerElement.nodeName === 'INPUT';
        this.disableBodyScroll = (!this.isInput && this.isReferenceElementInHeader);
        this.isOnHover = openerElement.hasAttribute(DATA_DROPDOWN_ON_HOVER);
        this.triggerEventName = this.isInput ? 'focus' : 'click';
        if (this.isOnHover) {
            this.triggerEventName = 'mouseover';
        }
        this.boundTriggerHandler = (event) => {
            event.preventDefault();
            event.stopPropagation();

            if (!this.isActive()) {
                this.open();
            } else if (!this.isInput && !this.isOnHover) {
                this.close();
            }
        };
        openerElement.addEventListener(this.triggerEventName, this.boundTriggerHandler);
        console.debug('Dropdown init', this);

        // Open the dropdown if the openerElement (<input> mainly) was focused before Dropdown JS initialization.
        if (document.activeElement === openerElement) {
            this.open();
        }
    }

    isForID(id) {
        // noinspection OverlyComplexBooleanExpressionJS
        return (this.dropdownElement.id && this.dropdownElement.id === id) || (this.openerElement.id && this.openerElement.id === id);
    }

    triggerOpen() {
        if (!this.isActive()) {
            this.openerElement.focus();
            if (!this.isInput) {
                this.openerElement.click();
            }
        }
    }

    showBackdrop() {
        document.body.classList.add(CLASS_OVERLAY_ACTIVE);
        this.isOnHover
            ? document.body.classList.add(CLASS_OVERLAY_DISABLE)
            : document.body.classList.remove(CLASS_OVERLAY_DISABLE);
    }

    static hideBackdrop() {
        document.body.classList.remove(CLASS_OVERLAY_ACTIVE);
        document.body.classList.remove(CLASS_OVERLAY_DISABLE);
    }

    open() {
        this.showBackdrop();
        const delay = parseInt(this.openerElement.getAttribute(DATA_DROPDOWN_DELAY)) ?? 0;

        setTimeout(() => {
            if (openedDropdown?.dropdownElement.id === this.dropdownElement.id) {
                return;
            }

            closeOpenedDropdown();
            this.showBackdrop();

            openedDropdown = this;
            if (this.isReferenceElementInHeader) {
                window.scrollTo({top: 0, behavior: 'smooth'});
            }
            const offsetY = 1 + parseInt(window.getComputedStyle(this.dropdownElement).getPropertyValue('--kvikDefDropdownOffset').replace(/[^0-9\-]/, '') || '0');
            this.popperInstance = createPopper(this.referenceElement, this.dropdownElement, {
                strategy: 'fixed',
                placement: this.openerElement.getAttribute(DATA_DROPDOWN_PLACEMENT) || 'bottom-start',
                modifiers: [{
                    ...preventOverflow,
                    options: {
                        tether: false
                    }
                }, {
                    ...offset,
                    options: {
                        offset: [0, offsetY]
                    }
                }]
            });

            this.dropdownElement.classList.add(CLASS_ACTIVE);
            this.openerElement.classList.add(CLASS_ACTIVE);

            if (document.activeElement === this.openerElement && !(this.openerElement instanceof HTMLInputElement)) {
                document.activeElement.blur();
            }

            setTimeout(() => {
                this.dropdownElement.style.height = 'auto';
                if (this.disableBodyScroll) {
                    lock(this.dropdownContentElement);
                    const referenceClientRects = this.popperInstance.state.elements.reference.getClientRects()[0];
                    const dropdownOffsetHeight = referenceClientRects.top + referenceClientRects.height;
                    const height = window.innerHeight - dropdownOffsetHeight;
                    console.debug(this.popperInstance, window.innerHeight, referenceClientRects, dropdownOffsetHeight, height);
                    //this.dropdownElement.style.height = height + 'px';
                    this.dropdownElement.style.height = `calc(100dvh - ${dropdownOffsetHeight}px)`;
                }
                this.dropdownContentElement.classList.add(CLASS_ACTIVE);
            }, 50);

            try {
                document.dispatchEvent(new CustomEvent(EVENT_DROPDOWN_OPEN, {
                    detail: {
                        dropdownElement: this.dropdownElement,
                        referenceElement: this.referenceElement,
                        openerElement: this.openerElement,
                        dropdown: this
                    }
                }));
            } catch (e) {
            }
        }, delay);
    }

    close(immediate = false) {
        this.openerElement.classList.remove(CLASS_ACTIVE);
        this.dropdownElement.children[0].classList.remove(CLASS_ACTIVE);
        openedDropdown = null;

        Dropdown.hideBackdrop();
        clearBodyLocks();

        setTimeout(() => {
            this.dropdownElement.classList.remove(CLASS_ACTIVE);
            if (this.popperInstance) {
                this.popperInstance.destroy();
            }
            this.popperInstance = null;
        }, immediate ? 0 : 250);

        try {
            document.dispatchEvent(new CustomEvent(EVENT_DROPDOWN_CLOSE, {
                detail: {
                    dropdownElement: this.dropdownElement,
                    openerElement: this.openerElement,
                    referenceElement: this.referenceElement,
                    dropdown: this
                }
            }));
        } catch (e) {
        }
    }

    isActive() {
        return this.openerElement.classList.contains(CLASS_ACTIVE);
    }

    isElementInsideOpenerOrReferenceOrDropdownElement(element) {
        if (this.openerElement === element || this.openerElement.contains(element)) {
            return true;
        }
        if (this.referenceElement === element || this.referenceElement.contains(element)) {
            return true;
        }

        return this.dropdownContentElement === element || this.dropdownContentElement.contains(element);
    }

    isOpenerInputFocused() {
        return Boolean(this.openerElement && this.isInput && document.activeElement === this.openerElement);
    }

}



window.addEventListener('click', (event) => {
    const targetElement = event.target;
    if (openedDropdown) {
        // Keep the dropdown open while its opener input is still focused (Safari dispatches clicks with
        // targets outside the input, e.g. the wrapping form, after the focus event opened the dropdown).
        if (openedDropdown.isOpenerInputFocused()) {
            return;
        }
        if (!openedDropdown.isElementInsideOpenerOrReferenceOrDropdownElement(targetElement)) {
            closeOpenedDropdown();
        }
    }
});
let lastWindowWidth = window.innerWidth;



window.addEventListener('resize', () => {
    if (window.innerWidth !== lastWindowWidth) {
        if (!openedDropdown?.isOpenerInputFocused()) {
            closeOpenedDropdown();
        }
        lastWindowWidth = window.innerWidth;
    }
}, {passive: true});



function locationHashChange() {
    console.debug(location.hash);
    const id = location.hash.slice(1);
    if (!id) {
        return;
    }

    for (const dropdown of allDropdowns) {
        if (dropdown && dropdown.isForID(location.hash.slice(1))) {
            dropdown.triggerOpen();
            location.hash = '';
            break;
        }
    }
}



/**
 * @param {HTMLElement} referenceElement
 */
function replaceRtlPlacement(referenceElement) {
    let placement = referenceElement.getAttribute(DATA_DROPDOWN_PLACEMENT) || 'bottom-start';
    const replacements = {
        end: 'start',
        start: 'end'
    };
    placement = placement.replace(/start|end/g, matched => replacements[matched]);
    referenceElement.setAttribute(DATA_DROPDOWN_PLACEMENT, placement);
}


function removeDropdown(id) {
    for (let i = 0; i < allDropdowns.length; i++) {
        const dropdown = allDropdowns[i];
        if (dropdown.isForID(id)) {
            dropdown.openerElement.removeAttribute(DATA_DROPDOWN_ID);
            dropdown.openerElement.removeAttribute(DATA_DROPDOWN_INIT);
            dropdown.dropdownElement = null;
            dropdown.openerElement.removeEventListener(dropdown.triggerEventName, dropdown.boundTriggerHandler);
            if (dropdown.popperInstance) {
                dropdown.popperInstance.destroy();
            }
            dropdown.popperInstance = null;
            dropdown.dropdownContentElement = null;
            dropdown.openerElement = null;
            dropdown.referenceElement = null;

            allDropdowns.splice(i, 1);

            return;
        }
    }
}

window.kvik_removeDropdown = removeDropdown;


onDOMReady(() => {
    const openerElements = document.querySelectorAll(`[${DATA_DROPDOWN_ID}]`);
    const isRtl = document.documentElement.getAttribute('dir') === 'rtl';

    for (const openerElement of openerElements) {
        if (openerElement.hasAttribute(DATA_DROPDOWN_INIT)) {
            continue;

        }
        if (isRtl) {
            replaceRtlPlacement(openerElement);
        }

        /** @type HTMLElement */
        const dropdownElement = document.getElementById(openerElement.dataset.dropdownId);
        createDropdown(openerElement, dropdownElement);
    }

    document.addEventListener('kvik_event_modal_show_modal_before', function () {
        closeOpenedDropdown(true);
    });

    window.addEventListener('hashchange', locationHashChange);
    locationHashChange();
});

// jQuery: few selector usages remaining